import React, { useState, useEffect, ComponentProps } from 'react'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { useThemeStyling } from '../../../hooks/useThemeStyling/useThemeStyling'
import { bytesToSize } from '../../../utils/bytesToSize'
import {
    AlertSnackbar,
    SearchableSelect,
    FileDnD,
    Icon,
    UploadCloudArrowUpIcon,
} from '../../../components'
import { alertSnackbarContentProps } from '../../../components/AlertSnackbar'
import { CaseBuilderIssuerFormLabel } from '../../../components/CaseBuilderIssuer/steps/InitiateDisputeStep1/InitiateDisputeStep1'
import pdfIcon from '../../../assets/icons/icon-pdf.svg'
import jpgIcon from '../../../assets/icons/icon-jpg.svg'
import pngIcon from '../../../assets/icons/icon-png.svg'
import { updatedToRGBA } from '../../../components/CaseBuilderIssuer/steps/InitiateDisputeStep1/InitiateDisputeStep1'
import {
    CaseReivewDialog,
    CaseComments,
    ConfirmationModal,
} from '../CaseReviewComponents'

interface UploadCaseDocFormData {
    template: string | undefined
    comment: string
    uploadedFiles: any[]
}

interface UploadCaseDocContentProps {
    onFormChange: (data: UploadCaseDocFormData) => void
    commentError: string | undefined
    commentRequired: boolean
    comment: string
    setComment: (comment: string) => void
}

const UploadCaseDocContent = ({
    onFormChange,
    commentError,
    commentRequired,
    comment,
    setComment,
}: UploadCaseDocContentProps) => {
    const { clientTheme } = useThemeStyling()

    const [alertSnackbarOpen, setAlertSnackbarOpen] = useState<boolean>(false)
    const [
        alertSnackbarProps,
        setAlertSnackbarProps,
    ] = useState<alertSnackbarContentProps>({})

    const [template, setTemplate] = useState<string>()
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([])

    useEffect(() => {
        onFormChange({
            template,
            comment,
            uploadedFiles,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, comment, uploadedFiles])

    const fileIcons: { [key: string]: any } = {
        'application/pdf': pdfIcon,
        'image/jpeg': jpgIcon,
        'image/png': pngIcon,
    }

    const fileTemplateList: { id: number; name: string; file: any }[] = [
        {
            name: 'Issuer Doc template A.pdf',
            id: 213,
            file: {
                id: 1973,
                filename: 'Issuer Doc template A.pdf',
                s3_key: 'templates/1/5fe67f8726ed5e91486e2a99cef8d5aa.pdf',
                content_type: 'application/pdf',
                size: 24753,
                date_created: '2024-07-23T19:45:53+00:00',
            },
        },
        {
            name: 'Issuer Doc template B.pdf',
            id: 225,
            file: {
                id: 1985,
                filename: 'Issuer Doc template B.pdf',
                s3_key: 'templates/1/5fe67f8726ed5e91486e2a99cef8d5aa.pdf',
                content_type: 'application/pdf',
                size: 24753,
                date_created: '2024-07-24T14:38:22+00:00',
            },
        },
    ]

    const handleFileDragAndDrop = (files: File[]) => {
        if (!files.length) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'Only a single PDF, JPG, or PNG file is allowed.',
                intent: 'error',
            })
            return setAlertSnackbarOpen(true)
        }
        const isDuplicateUpload = uploadedFiles.some(
            (file: File) =>
                file.name === files[0].name && file.size === files[0].size
        )
        if (isDuplicateUpload) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'A duplicate file has been uploaded.',
                intent: 'error',
            })
            return setAlertSnackbarOpen(true)
        }
        setUploadedFiles([...uploadedFiles, files[0]])
    }

    const handleUnstageFile = (selectedFileName: string) => {
        const updatedUploadFiles = uploadedFiles.filter(
            (file: File) => file.name !== selectedFileName
        )
        setUploadedFiles(updatedUploadFiles)
    }

    const handleAddTemplate = (template: any) => {
        const isDuplicateUpload = uploadedFiles.some(
            (file: File) =>
                file.name === template.name && file.size === template.file?.size
        )
        if (isDuplicateUpload) {
            setAlertSnackbarProps({
                title: 'Error',
                message: 'A duplicate file has been uploaded.',
                intent: 'error',
            })
            setAlertSnackbarOpen(true)
        } else {
            const templateFile = {
                id: template.id,
                name: template.name,
                size: template.file?.size,
                type: template.file?.content_type,
            }
            setTemplate(template.name)
            setUploadedFiles([templateFile, ...uploadedFiles])
        }
    }

    return (
        <>
            <p>
                Upload or select case documents and include comments to support
                your dispute challenge.
            </p>

            <Box
                sx={(theme) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(1),
                })}
            >
                <div>
                    <CaseBuilderIssuerFormLabel labelText={'Select Template'} />
                    <SearchableSelect
                        value={template}
                        onValueChange={handleAddTemplate}
                        placeholder={'Select template'}
                        options={fileTemplateList}
                        accessor={'name'}
                        testId={'fileTemplate'}
                        hideSearch
                        selectFontSize={clientTheme.typography.body1.fontSize}
                        isPaginate={false}
                    />
                </div>

                <FileDnD
                    accepts={['.jpg', '.pdf', '.png']}
                    onDrop={handleFileDragAndDrop}
                    iconComponent={
                        <UploadCloudArrowUpIcon
                            color={clientTheme.caseBuilder.color}
                            className="emp-additionalInfoStep3-uploadIcon"
                        />
                    }
                    fileUploadInstructionText={
                        <>
                            Drag & Drop here or{' '}
                            <span style={{ textDecoration: 'underline' }}>
                                Choose File
                            </span>
                        </>
                    }
                    fileUploadCriteria={`Supported Formats: pdf, jpg, png`}
                    enableFileDnDSlimStyling
                    hoverTheming={{
                        labelColor: clientTheme.caseBuilder.color,
                        borderColor: clientTheme.caseBuilder.color,
                        backgroundColor: updatedToRGBA(
                            clientTheme.caseBuilder.color,
                            0.05
                        ),
                    }}
                />

                <Box
                    sx={(theme) => ({
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: theme.spacing(1),
                    })}
                >
                    {uploadedFiles.map((file: File) => (
                        <Box
                            key={`key-file-${file.name}`}
                            sx={(theme) => ({
                                display: 'flex',
                                flexDirection: 'row',
                                gap: theme.spacing(1),
                                borderRadius: '4px',
                                padding: theme.spacing(1),
                                backgroundColor: 'rgba(221, 226, 235, 0.20)',
                            })}
                        >
                            {fileIcons[file.type] && (
                                <img
                                    src={fileIcons[file.type]}
                                    alt={file.type}
                                />
                            )}

                            <Box
                                sx={() => ({
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                })}
                            >
                                <Typography style={{ fontWeight: 600 }}>
                                    {file.name}
                                </Typography>
                                <Box
                                    sx={() => ({
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        '& > p': {
                                            fontSize: '12px',
                                        },
                                    })}
                                >
                                    <Typography style={{ color: '#A09E9E' }}>
                                        {`${bytesToSize(file.size)}`}
                                    </Typography>

                                    <Box
                                        sx={(theme) => ({
                                            width: '2px',
                                            height: '2px',
                                            backgroundColor:
                                                'rgba(38, 50, 56, 0.50)',
                                            borderRadius: '50%',
                                            display: 'inline-block',
                                            margin: theme.spacing(0, 1),
                                        })}
                                    />

                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        style={{
                                            fontSize: '10px',
                                            color: '#27AE60',
                                            marginRight: '4px',
                                        }}
                                    />
                                    <Typography>Upload Complete</Typography>
                                </Box>
                            </Box>

                            <Icon
                                className={`icon-trash-can emp-additionalInfoStep3-trashIcon`}
                                onClick={() => handleUnstageFile(file.name)}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            <CaseComments
                onChange={(e) => setComment(e.target.value)}
                error={commentError}
                required={commentRequired}
            />

            <AlertSnackbar
                content={alertSnackbarProps}
                open={alertSnackbarOpen}
                onClose={() => setAlertSnackbarOpen(false)}
                showCloseIcon
            />
        </>
    )
}

interface UploadCaseDocModalProps
    extends Omit<ComponentProps<typeof CaseReivewDialog>, 'title'> {
    caseId: string
    commentRequired?: boolean
}

export const UploadCaseDocModal = (props: UploadCaseDocModalProps) => {
    const [formData, setFormData] = useState<UploadCaseDocFormData>({
        template: undefined,
        comment: '',
        uploadedFiles: [],
    })
    const [commentError, setCommentError] = useState<string>()
    const [comment, setComment] = useState<string>('')

    const [
        openAreYouSureCloseModal,
        setOpenAreYouSureCloseModal,
    ] = useState<boolean>(false)

    function handleCancel() {
        const isDirty =
            formData.comment !== '' || formData.uploadedFiles.length > 0

        if (isDirty) {
            setOpenAreYouSureCloseModal(true)
        } else {
            props.secondaryButton.onClick()
            setComment('')
        }
    }

    function handleSubmit() {
        if (formData.comment || !props.commentRequired) {
            props.primaryButton.onClick()
        } else {
            setCommentError('Comment is required')
        }
    }

    function handleCancelClose() {
        setOpenAreYouSureCloseModal(false)
    }

    function handleSureToDiscard() {
        props.secondaryButton.onClick()
        setOpenAreYouSureCloseModal(false)
        setComment('')
    }

    function handleFormChange(data: UploadCaseDocFormData) {
        setFormData(data)
        setCommentError(undefined)
    }

    return (
        <>
            <CaseReivewDialog
                {...props}
                title={undefined}
                maxWidth="md"
                secondaryButton={{
                    text: props.secondaryButton.text,
                    onClick: handleCancel,
                }}
                primaryButton={{
                    text: props.primaryButton.text,
                    onClick: handleSubmit,
                    disabled: !comment?.length,
                }}
            >
                <h2
                    className="emp-modal-dialogTitle"
                    style={{ marginBottom: 0 }}
                >
                    Upload Case Doc - {props.caseId}
                </h2>
                <UploadCaseDocContent
                    onFormChange={handleFormChange}
                    commentError={commentError}
                    commentRequired={props.commentRequired ?? false}
                    comment={comment}
                    setComment={setComment}
                />
            </CaseReivewDialog>

            <ConfirmationModal
                open={openAreYouSureCloseModal}
                title={'Are You Sure?'}
                message={'The changes you made in this session will be lost.'}
                primaryButton={{
                    text: 'Discard',
                    onClick: handleSureToDiscard,
                }}
                secondaryButton={{ text: 'Cancel', onClick: handleCancelClose }}
            />
        </>
    )
}
